(() => {
  if (window.SPEECHCardSwatchControllerLoaded) return;
  window.SPEECHCardSwatchControllerLoaded = true;

  const cache = new Map();
  const normalise = (value) => String(value || '').trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
  const mediaText = (media) => normalise([media?.alt, media?.src, media?.preview_image?.src, media?.preview_image?.url].filter(Boolean).join(' '));
  const mediaSrc = (media) => media?.preview_image?.src || media?.preview_image?.url || media?.src || '';
  const colourTokens = (colour) => {
    const full = normalise(colour);
    return [full, ...full.split(' ').filter((token) => token.length >= 4)].filter(Boolean);
  };

  const colourMap = {
    black: '#111111', 'midnight black': '#111111', 'onyx black': '#111111', 'carbon black': '#242424', 'faded black': '#424243',
    white: '#ffffff', 'alabaster white': '#f7f5f0', bone: '#e8dfcf', 'faded bone': '#eae7e3', cream: '#f4efe5', ivory: '#f5f1e8',
    grey: '#888888', gray: '#888888', 'faded grey': '#787471', 'faded gray': '#787471', 'heather grey': '#a6a6a6', charcoal: '#454545', stone: '#b9b1a3', sand: '#cbb89b',
    navy: '#17233b', 'deep navy': '#17233b', blue: '#355c8a', red: '#b9282f', 'heritage red': '#b9282f',
    green: '#4f6b50', 'electric green': '#45d64a', olive: '#6d7140', sage: '#9aa78d', pink: '#d39aaa', 'pink joy': '#d39aaa'
  };

  function cssColour(name) {
    const key = normalise(name);
    if (colourMap[key]) return colourMap[key];
    if (window.CSS?.supports?.('color', name)) return name;
    return '#777777';
  }

  function ensureStyles() {
    if (document.getElementById('SpeechCardSwatchStyles')) return;
    const style = document.createElement('style');
    style.id = 'SpeechCardSwatchStyles';
    style.textContent = `
      .speech-card-swatches{display:flex;align-items:center;flex-wrap:wrap;gap:.55rem;margin:.75rem 0 .15rem;position:relative;z-index:5;min-height:1.5rem}
      .speech-card-swatch{appearance:none;-webkit-appearance:none;width:1.45rem;height:1.45rem;flex:0 0 1.45rem;border-radius:50%;padding:0;margin:0;background:var(--speech-card-swatch,#777);border:1px solid rgba(var(--color-foreground),.32);cursor:pointer;position:relative}
      .speech-card-swatch.is-active{outline:1.5px solid rgb(var(--color-foreground));outline-offset:2px}
      .speech-card-swatch[data-light='true']{border-color:rgba(var(--color-foreground),.55);box-shadow:inset 0 0 0 1px rgba(0,0,0,.08)}
      @media(max-width:749px){.speech-card-swatches{gap:.5rem;margin-top:.65rem}.speech-card-swatch{width:1.3rem;height:1.3rem;flex-basis:1.3rem}}
    `;
    document.head.appendChild(style);
  }

  async function fetchProduct(path) {
    if (!path) return null;
    if (!cache.has(path)) {
      cache.set(path, fetch(`${path}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then((response) => response.ok ? response.json() : null)
        .catch(() => null));
    }
    return cache.get(path);
  }

  function getColourIndex(product) {
    return Array.isArray(product?.options)
      ? product.options.findIndex((option) => /^(colour|color)$/i.test(String(option?.name || option || '')))
      : -1;
  }

  function colourValue(variant, index) {
    return variant?.[`option${index + 1}`] || variant?.options?.[index] || '';
  }

  function getColours(product, index) {
    if (index < 0) return [];
    const option = product.options?.[index];
    if (Array.isArray(option?.values) && option.values.length) {
      return option.values.map((value) => typeof value === 'string' ? value : value?.name).filter(Boolean);
    }
    const seen = new Set();
    return (product.variants || []).map((variant) => colourValue(variant, index)).filter((value) => {
      const key = normalise(value);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function variantMediaId(variant) {
    return variant?.featured_media?.id || variant?.featured_image?.id || variant?.featured_media_id || null;
  }

  function deriveGroups(product, colourIndex, colours) {
    const media = Array.isArray(product?.media) ? product.media : [];
    if (colourIndex < 0 || colours.length < 2 || media.length < 2) return {};
    const positionById = new Map(media.map((item, index) => [String(item.id), index]));

    let starts = colours.map((colour) => {
      const variant = (product.variants || []).find((candidate) => {
        const id = variantMediaId(candidate);
        return normalise(colourValue(candidate, colourIndex)) === normalise(colour) && id && positionById.has(String(id));
      });
      const id = variantMediaId(variant);
      return id ? { colour, position: positionById.get(String(id)) } : null;
    });

    if (!starts.every(Boolean)) {
      starts = colours.map((colour) => {
        const tokens = colourTokens(colour);
        const position = media.findIndex((item) => tokens.some((token) => mediaText(item).includes(token)));
        return position >= 0 ? { colour, position } : null;
      });
    }

    starts = starts.filter(Boolean).sort((a, b) => a.position - b.position);
    const unique = [];
    const used = new Set();
    starts.forEach((entry) => {
      if (!used.has(entry.position)) {
        used.add(entry.position);
        unique.push(entry);
      }
    });
    if (unique.length < 2) return {};

    const groups = {};
    unique.forEach((entry, index) => {
      const start = index === 0 ? 0 : entry.position;
      const end = unique[index + 1]?.position ?? media.length;
      groups[normalise(entry.colour)] = media.slice(start, end);
    });
    return groups;
  }

  function imageKey(url) {
    if (!url) return '';
    try {
      const parsed = new URL(url, location.origin);
      const filename = decodeURIComponent(parsed.pathname.split('/').pop() || '').toLowerCase();
      return filename.replace(/_(?:\d+x\d+|\d+x|x\d+)(?=\.[^.]+$)/, '');
    } catch (_) {
      const filename = String(url).split('?')[0].split('/').pop().toLowerCase();
      return filename.replace(/_(?:\d+x\d+|\d+x|x\d+)(?=\.[^.]+$)/, '');
    }
  }

  function mediaForImage(product, image) {
    const key = imageKey(image?.currentSrc || image?.src);
    if (!key) return null;
    return (product?.media || []).find((media) => {
      const candidate = imageKey(mediaSrc(media));
      return candidate && (candidate === key || candidate.startsWith(key) || key.startsWith(candidate));
    }) || null;
  }

  function classifyRole(media, image) {
    const text = normalise([mediaText(media), image?.alt].filter(Boolean).join(' '));
    if (/product flat back|flat back/.test(text)) return 'flat-back';
    if (/back hero|back lifestyle|back detail|rear/.test(text)) return 'back';
    if (/product flat front|flat front/.test(text)) return 'flat-front';
    if (/hero front|front hero|front lifestyle|front detail|01 hero/.test(text)) return 'front';
    return 'generic';
  }

  function roleMatch(media, role) {
    const text = mediaText(media);
    if (role === 'flat-back') return /product flat back|flat back/.test(text);
    if (role === 'back') return /back hero|back lifestyle|back detail|rear/.test(text) && !/flat back/.test(text);
    if (role === 'flat-front') return /product flat front|flat front/.test(text);
    if (role === 'front') return /hero front|front hero|front lifestyle|front detail|01 hero/.test(text) && !/flat front/.test(text);
    return false;
  }

  function pickMediaForRole(product, colour, groups, role, excludedUrl = '') {
    const all = Array.isArray(product?.media) ? product.media : [];
    const grouped = groups[normalise(colour)]?.length ? groups[normalise(colour)] : all;
    const tokens = colourTokens(colour);
    const colourMatched = grouped.filter((item) => tokens.some((token) => mediaText(item).includes(token)));
    const candidates = colourMatched.length ? colourMatched : grouped;
    const excludedKey = imageKey(excludedUrl);
    const eligible = candidates.filter((item) => !excludedKey || imageKey(mediaSrc(item)) !== excludedKey);

    return eligible.find((item) => roleMatch(item, role))
      || candidates.find((item) => roleMatch(item, role))
      || eligible[0]
      || candidates[0]
      || null;
  }

  function snapshotImage(image) {
    return {
      src: image.getAttribute('src') || image.src || '',
      srcset: image.getAttribute('srcset'),
      sizes: image.getAttribute('sizes'),
      alt: image.getAttribute('alt'),
    };
  }

  function restoreImage(image, snapshot) {
    if (!image || !snapshot) return;
    if (snapshot.src) image.setAttribute('src', snapshot.src);
    else image.removeAttribute('src');
    if (snapshot.srcset == null) image.removeAttribute('srcset');
    else image.setAttribute('srcset', snapshot.srcset);
    if (snapshot.sizes == null) image.removeAttribute('sizes');
    else image.setAttribute('sizes', snapshot.sizes);
    if (snapshot.alt == null) image.removeAttribute('alt');
    else image.setAttribute('alt', snapshot.alt);
  }

  function setImage(image, url, alt) {
    if (!image || !url) return;
    image.src = url;
    image.removeAttribute('srcset');
    if (alt) image.alt = alt;
  }

  function variantForColour(product, colourIndex, colour) {
    const matches = (product.variants || []).filter((variant) => normalise(colourValue(variant, colourIndex)) === normalise(colour));
    return matches.find((variant) => variant.available) || matches[0] || null;
  }

  function currentVariant(product, card, path) {
    for (const link of card.querySelectorAll('a[href*="/products/"]')) {
      try {
        const parsed = new URL(link.href, location.origin);
        if (parsed.pathname !== path) continue;
        const id = parsed.searchParams.get('variant');
        const variant = (product.variants || []).find((item) => String(item.id) === String(id));
        if (variant) return variant;
      } catch (_) {}
    }
    return (product.variants || []).find((variant) => variant.available) || product.variants?.[0] || null;
  }

  function snapshotLinks(card) {
    return {
      anchors: Array.from(card.querySelectorAll('a[href*="/products/"]')).map((anchor) => ({ anchor, href: anchor.getAttribute('href') })),
      dataUrls: Array.from(card.querySelectorAll('[data-product-url]')).map((node) => ({ node, value: node.dataset.productUrl })),
    };
  }

  function restoreLinks(snapshot) {
    snapshot?.anchors?.forEach(({ anchor, href }) => {
      if (href == null) anchor.removeAttribute('href');
      else anchor.setAttribute('href', href);
    });
    snapshot?.dataUrls?.forEach(({ node, value }) => {
      if (value == null) delete node.dataset.productUrl;
      else node.dataset.productUrl = value;
    });
  }

  function syncLinks(card, path, variantId) {
    const target = `${path}?variant=${variantId}`;
    card.querySelectorAll('a[href*="/products/"]').forEach((anchor) => {
      try {
        if (new URL(anchor.href, location.origin).pathname === path) anchor.href = target;
      } catch (_) {}
    });
    card.querySelectorAll('[data-product-url]').forEach((node) => {
      try {
        if (new URL(node.dataset.productUrl || '', location.origin).pathname === path) node.dataset.productUrl = target;
      } catch (_) {}
    });
  }

  async function enhanceCard(card) {
    if (!card || card.dataset.speechCardSwatches === 'loading' || card.dataset.speechCardSwatches === 'ready') return;
    const heading = card.querySelector('.card__information .card__heading');
    const link = heading?.querySelector('a[href*="/products/"]') || card.querySelector('a.full-unstyled-link[href*="/products/"]');
    const images = Array.from(card.querySelectorAll('.card__media img'));
    if (!heading || !link || !images.length) return;

    card.dataset.speechCardSwatches = 'loading';
    let path;
    try { path = new URL(link.href, location.origin).pathname; } catch (_) {
      card.dataset.speechCardSwatches = 'none';
      return;
    }

    const imageSnapshots = images.map(snapshotImage);
    const linkSnapshot = snapshotLinks(card);
    const product = await fetchProduct(path);
    const colourIndex = getColourIndex(product);
    const colours = getColours(product, colourIndex);
    if (!product || colourIndex < 0 || colours.length < 2) {
      card.dataset.speechCardSwatches = 'none';
      return;
    }

    const groups = deriveGroups(product, colourIndex, colours);
    const initialVariant = currentVariant(product, card, path);
    const initialColour = colourValue(initialVariant, colourIndex) || colours[0];
    const originalMedia = images.map((image) => mediaForImage(product, image));
    const originalRoles = images.map((image, index) => classifyRole(originalMedia[index], image));
    const wrap = document.createElement('div');
    wrap.className = 'speech-card-swatches';
    wrap.setAttribute('role', 'group');
    wrap.setAttribute('aria-label', `Available colours: ${colours.join(', ')}`);

    const setActiveSwatch = (colour) => {
      wrap.querySelectorAll('.speech-card-swatch').forEach((button) => {
        const active = normalise(button.dataset.colour) === normalise(colour);
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    };

    const apply = (colour) => {
      if (normalise(colour) === normalise(initialColour)) {
        images.forEach((image, index) => restoreImage(image, imageSnapshots[index]));
        restoreLinks(linkSnapshot);
        setActiveSwatch(colour);
        return;
      }

      const variant = variantForColour(product, colourIndex, colour);
      const primaryMedia = pickMediaForRole(product, colour, groups, originalRoles[0]);
      const primaryUrl = mediaSrc(primaryMedia)
        || (typeof variant?.featured_image === 'string' ? variant.featured_image : variant?.featured_image?.src || '');
      setImage(images[0], primaryUrl, imageSnapshots[0]?.alt || `${product.title} - ${colour}`);

      if (images[1]) {
        const secondaryMedia = pickMediaForRole(product, colour, groups, originalRoles[1], primaryUrl);
        const secondaryUrl = mediaSrc(secondaryMedia) || primaryUrl;
        setImage(images[1], secondaryUrl, imageSnapshots[1]?.alt || `${product.title} - ${colour} alternate`);
      }

      if (variant?.id) syncLinks(card, path, variant.id);
      setActiveSwatch(colour);
    };

    colours.forEach((colour) => {
      const key = normalise(colour);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'speech-card-swatch';
      button.dataset.colour = colour;
      button.title = colour;
      button.setAttribute('aria-label', `Show ${colour}`);
      button.setAttribute('aria-pressed', 'false');
      button.style.setProperty('--speech-card-swatch', cssColour(colour));
      if (['white', 'alabaster white', 'bone', 'cream', 'ivory'].includes(key)) button.dataset.light = 'true';
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        apply(colour);
      });
      wrap.appendChild(button);
    });

    card.querySelector('.speech-card-swatches')?.remove();
    heading.insertAdjacentElement('afterend', wrap);
    setActiveSwatch(initialColour);
    card.dataset.speechCardSwatches = 'ready';
  }

  function scan(root = document) {
    if (root.matches?.('.product-card-wrapper')) enhanceCard(root);
    root.querySelectorAll?.('.product-card-wrapper').forEach(enhanceCard);
  }

  ensureStyles();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  else scan();
  document.addEventListener('shopify:section:load', (event) => scan(event.target));

  new MutationObserver((records) => {
    records.forEach((record) => record.addedNodes.forEach((node) => {
      if (node instanceof Element) scan(node);
    }));
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
